import React from 'react';
import { ClearingSummaryTable, Currency as CurrencyType } from '@card/types';
import {
  Currency,
  InputLabel,
  Td,
  TableRow,
  H3,
  Table as SingleHeaderTable
} from '@increasecard/typed-components';
import {
  TableOuterWrapper,
  TableInnerWrapper,
  Table,
  Th,
  RowTh,
  TableHeaderRow,
  TableHeaderTh,
  TableTotalRow,
  HeaderVariant,
  TotalRowTh,
  LoadingOverlay,
  CountryFilter
} from '@card/commonlib';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { Country } from '@increasecard/increase-core';
import { Loadable, LoadableStatus } from '@card/types';

export interface DailyListProps {
  clearings: Loadable<ClearingSummaryTable[], Error> | undefined;
  currency: CurrencyType | undefined;
  country: Country;
}

export function DailyList({ clearings, currency, country }: DailyListProps) {
  const { t } = useTranslation('clearings');

  if (!clearings || !currency || !country) {
    return null;
  }

  const { data: clearingsSummary } = clearings;
  const isLoading = clearings.status === LoadableStatus.Fetching;
  const hasData = clearingsSummary && clearingsSummary.length > 0;

  const ProvidersHeadings = () => {
    return (
      <>
        {clearingsSummary &&
          clearingsSummary.map((clearing: ClearingSummaryTable) => {
            return (
              <Th key={clearing.provider} rightAligned>
                {clearing.provider}
              </Th>
            );
          })}
      </>
    );
  };

  const renderRowByProvider = (label: string, key: string) => {
    return (
      <>
        {clearingsSummary &&
          clearingsSummary.map((clearing: ClearingSummaryTable) => {
            const value: number = get(clearing, key) || 0;
            return (
              <Td key={clearing.provider} rightAligned>
                <Currency currency={currency} value={value} />
              </Td>
            );
          })}
      </>
    );
  };

  const renderValueByProvider = (label: string, key: string) => {
    return (
      <TableRow>
        <RowTh>
          <InputLabel as="div">{label}</InputLabel>
        </RowTh>
        {renderRowByProvider(label, key)}
      </TableRow>
    );
  };

  const renderTotalByProvider = (label: string, key: string, variant: HeaderVariant) => {
    return (
      <TableTotalRow variant={variant}>
        <TotalRowTh variant={variant}>
          <InputLabel as="div">{label}</InputLabel>
        </TotalRowTh>
        {renderRowByProvider(label, key)}
      </TableTotalRow>
    );
  };

  const renderValueByProviderIfNotZero = (label: string, key: string) => {
    const hasValueNotZero: boolean | undefined =
      clearingsSummary &&
      clearingsSummary.some((clearing: ClearingSummaryTable) => {
        const value = get(clearing, key);
        return value && value !== 0;
      });
    if (hasValueNotZero) {
      return renderValueByProvider(label, key);
    }
    return null;
  };

  const renderPresented = () => {
    return (
      <>
        <TableHeaderRow variant="card">
          <TableHeaderTh variant="card">{t('daily.presented')}</TableHeaderTh>
          <ProvidersHeadings />
        </TableHeaderRow>
        <CountryFilter hideFor={['dominicana']}>
          {renderValueByProvider(t('daily.debit'), 'presented.debit')}
        </CountryFilter>
        <CountryFilter hideFor={['ecuador', 'dominicana']}>
          {renderValueByProvider(t('daily.credit'), 'presented.credit')}
        </CountryFilter>
        <CountryFilter hideFor={['dominicana']}>
          {renderValueByProvider(t('daily.creditInstallments'), 'presented.creditWithInstallments')}
        </CountryFilter>
        <CountryFilter hideFor={['uruguay', 'argentina', 'dominicana']}>
          {renderValueByProvider(
            t('daily.creditFinancedByMerchant'),
            'presented.creditFinancedByMerchant'
          )}
        </CountryFilter>
        <CountryFilter hideFor={['dominicana']}>
          {renderValueByProvider(t('daily.chargebacks'), 'presented.totalChargebacks')}
        </CountryFilter>
        <CountryFilter hideFor={['dominicana']}>
          {renderValueByProviderIfNotZero(t('daily.openingBalance'), 'presented.openingBalance')}
        </CountryFilter>
        {renderTotalByProvider(t('daily.subtotal'), 'totalAmount', 'card')}
      </>
    );
  };

  const renderDiscounts = () => {
    return (
      <>
        <TableHeaderRow variant="alert">
          <TableHeaderTh variant="alert">
            <H3>{t('daily.discounts')}</H3>
          </TableHeaderTh>
          <ProvidersHeadings />
        </TableHeaderRow>
        <CountryFilter hideFor={['ecuador', 'dominicana']}>
          {renderValueByProvider(t('daily.promotions'), 'discountAmounts.sellDiscount')}
        </CountryFilter>
        {renderValueByProvider(t('daily.arancels'), 'discountAmounts.arancel')}
        {renderValueByProvider(t('daily.taxes'), 'discountAmounts.taxes')}
        <CountryFilter hideFor={['ecuador', 'dominicana']}>
          {renderValueByProvider(t('daily.financialCost'), 'discountAmounts.financialCost')}
        </CountryFilter>
        <CountryFilter hideFor={['ecuador', 'dominicana']}>
          {renderValueByProvider(t('daily.others'), 'discountAmounts.others')}
        </CountryFilter>
        {renderTotalByProvider(t('daily.subtotal'), 'totalDeduction', 'alert')}
      </>
    );
  };

  const renderDeposited = () => {
    return (
      <>
        <TableHeaderRow variant="success">
          <TableHeaderTh variant="success">
            <H3>{t('daily.deposited')}</H3>
          </TableHeaderTh>
          <ProvidersHeadings />
        </TableHeaderRow>
        {renderTotalByProvider(t('daily.total'), 'totalEarn', 'success')}
        {renderValueByProviderIfNotZero(t('daily.closingBalance'), 'presented.closingBalance')}
      </>
    );
  };

  return (
    <>
      {isLoading || (!isLoading && hasData) ? (
        <LoadingOverlay active={isLoading} style={{ top: '1px', left: '-1px' }}>
          <TableOuterWrapper>
            <TableInnerWrapper>
              <Table>
                <tbody>
                  {renderPresented()}
                  {renderDiscounts()}
                  {renderDeposited()}
                </tbody>
              </Table>
            </TableInnerWrapper>
          </TableOuterWrapper>
        </LoadingOverlay>
      ) : (
        <SingleHeaderTable hasData={false}>
          <br />
        </SingleHeaderTable>
      )}
    </>
  );
}
